import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios, { AxiosResponse } from 'axios';
import { authAPI, JobSeeker, CompanyProfile } from '../services/api';

interface AuthContextType {
  user: JobSeeker | CompanyProfile | null;
  userType: 'jobseeker' | 'company' | null;
  login: (email: string, password: string) => Promise<any>;
  register: (userData: any) => Promise<AxiosResponse<any>>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<JobSeeker | CompanyProfile | null>(null);
  const [userType, setUserType] = useState<'jobseeker' | 'company' | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      const profileData = response.data;
      console.log('Fetched profile data:', profileData);

      setUser(profileData);
      if (profileData.role === 'JOB_SEEKER') {
        setUserType('jobseeker');
      } else if (profileData.role === 'COMPANY') {
        setUserType('company');
      }
      setError(null);
    } catch (error: any) {
      console.error('Failed to fetch user profile:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setUserType(null);
      setError('Session expired. Please login again.');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<any> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authAPI.login(email, password);
      const { accessToken, refreshToken, user: userData } = response.data;
      console.log('User data from login response:', userData);
      
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      setUser(userData);
      if (userData.role === 'JOB_SEEKER') {
        setUserType('jobseeker');
      } else if (userData.role === 'COMPANY') {
        setUserType('company');
      }
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: any): Promise<AxiosResponse<any>> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authAPI.register(userData);
      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setUserType(null);
      setError(null);
    }
  };

  const value: AuthContextType = {
    user,
    userType,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    loading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};